import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Dropdown, List, Menu, Row, Space, Typography } from 'antd';
import {
    TbMessageCheck,
    TbMessageCircle,
    TbMessageQuestion,
    TbMessages,
    TbSettings2,
    TbSortAscending,
    TbSortDescending,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useResourceCommentMutation } from '@/hooks/useResourceCommentMutation';
import { useWorkspaceResourceAccess } from '@/hooks/useWorkspaceResourceAccess';
import { resourceCommentService } from '@/services/database';
import { BaseSkeleton, DropdownOptionContent, EmptySection, OverflowFadeScrollArea } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useReportComponentStore, useUserStore } from '@/stores';
import { ResourceActivityResourceType, ResourceComment } from '@/types';
import type { CommentComposerValue } from '@/types';

import CommentComposer from '../CommentComposer';
import { isCommentableWorkspaceResource } from '../resourceCommentAccess';
import ResourceCommentCard from '../ResourceCommentCard';
import { childCommentsFor, upsertComment } from '../ResourceCommentCard/utils';
import {
    emitResourceCommentEvent,
    getOptimisticResourceComments,
    subscribeResourceActivityOpenEvents,
    subscribeResourceCommentEvents,
} from '../resourceCommentEvents';

interface ResourceCommentsPanelProps {
    canComment?: boolean;
    component?: string;
    focusedCommentId?: string;
    height?: number | string;
    resourceId: string;
    resourceTitle?: string;
    resourceType: ResourceActivityResourceType;
    resourceWebsite?: string;
}

type ResourceCommentOrder = 'newest' | 'oldest';
type ResourceCommentStatusFilter = 'all' | 'open' | 'resolved';

const ResourceCommentsPanel: FC<ResourceCommentsPanelProps> = ({
    canComment,
    component,
    focusedCommentId,
    height = '28rem',
    resourceId,
    resourceTitle,
    resourceType,
    resourceWebsite,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [comments, setComments] = useState<ResourceComment[]>([]);
    const [commentOrder, setCommentOrder] = useState<ResourceCommentOrder>('newest');
    const [statusFilter, setStatusFilter] = useState<ResourceCommentStatusFilter>('open');
    const [loading, setLoading] = useState(true);
    const [submittingReplyId, setSubmittingReplyId] = useState<string>();
    const [pendingFocusedCommentId, setPendingFocusedCommentId] = useState<string | undefined>(focusedCommentId);
    const [activeFocusedCommentId, setActiveFocusedCommentId] = useState<string>();
    const focusTimeoutRef = useRef<number | undefined>(undefined);
    const reportComponentsById = useReportComponentStore((state) => state.reportComponentsById);
    const currentUser = useUserStore((state) => state.selectedUser);
    const resourceAccess = useWorkspaceResourceAccess(
        isCommentableWorkspaceResource(resourceType) ? resourceType : 'investments',
        isCommentableWorkspaceResource(resourceType) ? resourceId : null,
    );
    const commentingAllowed =
        canComment ?? (isCommentableWorkspaceResource(resourceType) ? resourceAccess.canComment : false);
    const canModerateComments = ['Admin', 'Owner'].includes(currentUser?.activeMember?.permission ?? '');
    const commentMutation = useResourceCommentMutation({
        resourceId,
        resourceTitle,
        resourceType,
        resourceWebsite,
    });
    const replyMutation = useResourceCommentMutation({
        errorMessage: 'Could not create reply',
        resourceId,
        resourceTitle,
        resourceType,
        resourceWebsite,
    });

    const loadComments = useCallback(async () => {
        try {
            setLoading(true);
            const loadedComments = await resourceCommentService.list(resourceType, resourceId, {
                component,
                includeResolved: true,
            });
            const optimisticComments = getOptimisticResourceComments(resourceType, resourceId).filter(
                (comment) => !component || comment.component === component,
            );

            setComments(optimisticComments.reduce(upsertComment, loadedComments));
        } catch (error) {
            captureException(error);
        } finally {
            setLoading(false);
        }
    }, [component, resourceId, resourceType]);

    useEffect(() => {
        loadComments();
    }, [loadComments]);

    useEffect(() => {
        return subscribeResourceCommentEvents((detail) => {
            if (detail.resourceType !== resourceType || detail.resourceId !== resourceId) {
                return;
            }

            if (component && detail.comment && detail.comment.component !== component) {
                return;
            }

            if (detail.action === 'deleted' && detail.commentId) {
                setComments((previous) =>
                    previous.filter(
                        (comment) => comment.id !== detail.commentId && comment.parentId !== detail.commentId,
                    ),
                );
                return;
            }

            if (detail.comment) {
                setComments((previous) => upsertComment(previous, detail.comment as ResourceComment));
            }
        });
    }, [component, resourceId, resourceType]);

    const filteredComments = useMemo(() => {
        const parentComments = comments.filter((comment) => !comment.parentId);

        if (statusFilter === 'all') {
            return parentComments;
        }

        return parentComments.filter((comment) => (statusFilter === 'resolved' ? comment.resolved : !comment.resolved));
    }, [comments, statusFilter]);
    const visibleComments = useMemo(
        () =>
            [...filteredComments].sort((first, second) => {
                const firstTime = new Date(first.createdAt).getTime();
                const secondTime = new Date(second.createdAt).getTime();

                return commentOrder === 'newest' ? secondTime - firstTime : firstTime - secondTime;
            }),
        [commentOrder, filteredComments],
    );

    useEffect(() => {
        setPendingFocusedCommentId(focusedCommentId);
    }, [focusedCommentId]);

    useEffect(() => {
        return subscribeResourceActivityOpenEvents((detail) => {
            if (detail.resourceType !== resourceType || detail.resourceId !== resourceId || detail.tab !== 'comments') {
                return;
            }

            setPendingFocusedCommentId(detail.commentId);
        });
    }, [resourceId, resourceType]);

    useEffect(() => {
        if (!pendingFocusedCommentId || loading) {
            return;
        }

        const comment = comments.find((item) => item.id === pendingFocusedCommentId);
        const parentComment = comment?.parentId ? comments.find((item) => item.id === comment.parentId) : comment;

        if (!comment || !parentComment) {
            return;
        }

        if (
            statusFilter !== 'all' &&
            ((statusFilter === 'open' && parentComment.resolved) ||
                (statusFilter === 'resolved' && !parentComment.resolved))
        ) {
            setStatusFilter(parentComment.resolved ? 'resolved' : 'open');
            return;
        }

        window.requestAnimationFrame(() => {
            const element = document.querySelector<HTMLElement>(
                `[data-resource-comment-id="${pendingFocusedCommentId}"]`,
            );

            element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            setActiveFocusedCommentId(pendingFocusedCommentId);

            if (focusTimeoutRef.current) {
                window.clearTimeout(focusTimeoutRef.current);
            }

            focusTimeoutRef.current = window.setTimeout(() => {
                setActiveFocusedCommentId(undefined);
            }, 1600);
        });
    }, [comments, loading, pendingFocusedCommentId, statusFilter]);

    useEffect(() => {
        return () => {
            if (focusTimeoutRef.current) {
                window.clearTimeout(focusTimeoutRef.current);
            }
        };
    }, []);

    const createComment = async ({ mentions, message }: CommentComposerValue) => {
        const comment = await commentMutation.createComment({ component, mentions, message });

        return Boolean(comment);
    };

    const updateComment = async (commentId: string, resolved: boolean) => {
        const previousComment = comments.find((comment) => comment.id === commentId);

        if (!previousComment || !commentingAllowed) {
            return;
        }

        const optimisticComment = { ...previousComment, resolved, updatedAt: new Date() };

        setComments((previous) => previous.map((item) => (item.id === commentId ? optimisticComment : item)));
        emitResourceCommentEvent({ action: 'updated', comment: optimisticComment, resourceId, resourceType });

        try {
            const comment = await resourceCommentService.update(resourceType, resourceId, commentId, { resolved });

            setComments((previous) => previous.map((item) => (item.id === comment.id ? comment : item)));
            emitResourceCommentEvent({ action: 'updated', comment, resourceId, resourceType });
        } catch (error) {
            setComments((previous) =>
                previous.map((item) => (item.id === previousComment.id ? previousComment : item)),
            );
            emitResourceCommentEvent({ action: 'updated', comment: previousComment, resourceId, resourceType });
            captureException(error);
            toast.error('Could not update comment');
        }
    };

    const deleteComment = async (commentId: string) => {
        if (!commentingAllowed) {
            return;
        }

        const removedComments = comments.filter(
            (comment) => comment.id === commentId || comment.parentId === commentId,
        );

        setComments((previous) =>
            previous.filter((comment) => comment.id !== commentId && comment.parentId !== commentId),
        );
        emitResourceCommentEvent({ action: 'deleted', commentId, resourceId, resourceType });

        try {
            await resourceCommentService.delete(resourceType, resourceId, commentId);
        } catch (error) {
            setComments((previous) => removedComments.reduce(upsertComment, previous));
            removedComments.forEach((comment) =>
                emitResourceCommentEvent({ action: 'created', comment, resourceId, resourceType }),
            );
            captureException(error);
            toast.error('Could not delete comment');
        }
    };

    const createReply = async (parentComment: ResourceComment, value: CommentComposerValue) => {
        const input = {
            component: parentComment.component,
            mentions: value.mentions,
            message: value.message,
            metadata: {
                source: 'resource-comment-thread',
                target: {
                    parentCommentId: parentComment.id,
                    type: 'comment-reply',
                },
            },
            parentId: parentComment.id,
        };
        try {
            setSubmittingReplyId(parentComment.id);

            const comment = await replyMutation.createComment(input);

            return Boolean(comment);
        } finally {
            setSubmittingReplyId(undefined);
        }
    };

    const composer = (
        <CommentComposer
            disabled={!commentingAllowed}
            onSubmit={createComment}
            resourceId={resourceId}
            resourceType={resourceType}
            submitting={commentMutation.submitting}
        />
    );
    const commentOrderOptions = [
        {
            description: 'Newest comments appear at the bottom.',
            icon: <TbSortAscending size='1rem' style={{ color: token.colorTextTertiary }} />,
            label: 'Oldest First',
            value: 'oldest' as ResourceCommentOrder,
        },
        {
            description: 'Newest comments appear at the top.',
            icon: <TbSortDescending size='1rem' style={{ color: token.colorTextTertiary }} />,
            label: 'Newest First',
            value: 'newest' as ResourceCommentOrder,
        },
    ];
    const selectedCommentOrder = commentOrderOptions.find((option) => option.value === commentOrder);
    const commentStatusOptions = [
        {
            icon: <TbMessageQuestion size='1rem' />,
            label: 'Open',
            value: 'open' as ResourceCommentStatusFilter,
        },
        {
            icon: <TbMessageCheck size='1rem' />,
            label: 'Resolved',
            value: 'resolved' as ResourceCommentStatusFilter,
        },
        {
            icon: <TbMessages size='1rem' />,
            label: 'All',
            value: 'all' as ResourceCommentStatusFilter,
        },
    ];
    const commentOrderMenu = (
        <DropdownMenuPanel selectedKeys={[commentOrder]} style={{ minWidth: 16 * 16 }}>
            <div style={{ padding: '0 0.75rem', marginBottom: '0.25rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Order</small>
            </div>
            {commentOrderOptions.map((option) => (
                <Menu.Item key={option.value} onClick={() => setCommentOrder(option.value)}>
                    <DropdownOptionContent
                        description={option.description}
                        icon={option.icon}
                        selected={commentOrder === option.value}
                        title={option.label}
                    />
                </Menu.Item>
            ))}
        </DropdownMenuPanel>
    );
    const commentSettingsMenu = (
        <DropdownMenuPanel selectedKeys={[statusFilter]} style={{ minWidth: 16 * 14 }}>
            <div style={{ padding: '0 0.75rem', marginBottom: '0.25rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Status</small>
            </div>
            {commentStatusOptions.map((option) => (
                <Menu.Item key={option.value} onClick={() => setStatusFilter(option.value)}>
                    <DropdownOptionContent
                        icon={option.icon}
                        selected={statusFilter === option.value}
                        title={option.label}
                    />
                </Menu.Item>
            ))}
        </DropdownMenuPanel>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height, minHeight: 0 }}>
            <Row align='middle' wrap={false} style={{ marginBottom: '0.75rem' }}>
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <Typography.Title level={5} style={{ marginBottom: 0 }}>
                        <TbMessageCircle
                            size='1.125rem'
                            style={{ verticalAlign: '-0.2rem', marginRight: '0.375rem' }}
                        />
                        Comments
                    </Typography.Title>
                </Col>
                <Col flex='none'>
                    <Space.Compact size='small'>
                        <Dropdown trigger={['click']} placement='bottomRight' popupRender={() => commentOrderMenu}>
                            <DropdownButton size='small' icon={selectedCommentOrder?.icon}>
                                {selectedCommentOrder?.label}
                            </DropdownButton>
                        </Dropdown>
                        <Dropdown trigger={['click']} placement='bottomRight' popupRender={() => commentSettingsMenu}>
                            <Button
                                size='small'
                                icon={<TbSettings2 size='1rem' style={{ color: token.colorTextTertiary }} />}
                            />
                        </Dropdown>
                    </Space.Compact>
                </Col>
            </Row>
            <OverflowFadeScrollArea
                aria-label='Resource comments'
                containerStyle={{ flex: 1, minHeight: 0 }}
                fill
                style={{ paddingRight: '0.25rem' }}
                surface='container'
            >
                {loading ? (
                    <BaseSkeleton paragraph={{ rows: 5 }} />
                ) : visibleComments.length ? (
                    <List
                        dataSource={visibleComments}
                        style={{ width: '100%' }}
                        renderItem={(comment) => {
                            const replies = childCommentsFor(comments, comment.id);
                            const isCommentAuthor =
                                comment.user?.id === currentUser?.id ||
                                comment.member?.id === currentUser?.activeMember?.id;

                            return (
                                <List.Item data-resource-comment-id={comment.id} style={{ width: '100%' }}>
                                    <ResourceCommentCard
                                        active={activeFocusedCommentId === comment.id}
                                        comment={comment}
                                        onCreateReply={commentingAllowed ? createReply : undefined}
                                        onDelete={
                                            commentingAllowed && (isCommentAuthor || canModerateComments)
                                                ? deleteComment
                                                : undefined
                                        }
                                        onToggleResolved={
                                            commentingAllowed
                                                ? (nextComment) => updateComment(nextComment.id, !nextComment.resolved)
                                                : undefined
                                        }
                                        replies={replies}
                                        reportComponentsById={reportComponentsById}
                                        resourceId={resourceId}
                                        resourceTitle={resourceTitle}
                                        resourceType={resourceType}
                                        resourceWebsite={resourceWebsite}
                                        showReplyComposer={commentingAllowed}
                                        submittingReply={submittingReplyId === comment.id}
                                    />
                                </List.Item>
                            );
                        }}
                    />
                ) : (
                    <EmptySection
                        height='100%'
                        icon={<TbMessageCircle size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                        label='No Comments'
                        description='Comments and threads for this resource will appear here.'
                    />
                )}
            </OverflowFadeScrollArea>
            <div
                style={{
                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                    flex: 'none',
                    marginTop: '0.75rem',
                    paddingTop: '0.75rem',
                }}
            >
                {composer}
            </div>
        </div>
    );
};

export default ResourceCommentsPanel;
